import { useEffect, useState } from "react";
import { Scale, Sparkles, RefreshCw, AlertTriangle, CheckCircle2, Save, RotateCcw, ExternalLink, Edit, X } from "lucide-react";

import AdminLayout from "@/pages/admin/AdminLayout";
import { api } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SEVERITY_COLOR = {
  high: "bg-red-100 text-red-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-blue-100 text-blue-700",
};
const CONFIDENCE_COLOR = {
  hoog: "bg-emerald-100 text-emerald-700",
  gemiddeld: "bg-blue-100 text-blue-700",
  laag: "bg-amber-100 text-amber-700",
};

export default function AdminLegalRules() {
  const { toast } = useToast();
  const [rules, setRules] = useState([]);
  const [summary, setSummary] = useState(null);
  const [running, setRunning] = useState(false);
  const [editing, setEditing] = useState(null);

  async function load() {
    const [r, s] = await Promise.all([
      api.get("/admin/legal/rules"),
      api.get("/admin/legal/audit/summary"),
    ]);
    setRules(r.data.rules || []);
    setSummary(s.data);
  }
  useEffect(() => { load(); }, []);

  async function runFullAudit() {
    setRunning(true);
    toast({ title: "Audit gestart", description: "~30s per checker × regio — ongeveer 15 minuten voor alle 36 combinaties." });
    try {
      const r = await api.post("/admin/legal/audit/run-now");
      toast({ title: `Audit klaar — ${r.data.outdated}/${r.data.audited} mogelijk verouderd` });
      load();
    } catch (err) {
      toast({ title: "Audit faalde", description: err?.message || "Probeer opnieuw", variant: "destructive" });
    } finally {
      setRunning(false);
    }
  }

  async function auditOne(slug, jur) {
    toast({ title: `Audit ${slug} / ${jur}…` });
    try {
      const r = await api.post("/admin/legal/audit/run-one", { checker_slug: slug, jurisdiction: jur });
      toast({ title: r.data.is_outdated ? `${r.data.issues?.length || 0} issues gevonden` : "Geen issues gevonden" });
      load();
    } catch (err) {
      toast({ title: "Mislukt", variant: "destructive" });
    }
  }

  async function applySuggestion(slug, jur, field, value) {
    try {
      await api.post("/admin/legal/audit/apply-suggestion", {
        checker_slug: slug, jurisdiction: jur, field, new_value: value,
      });
      toast({ title: `Voorstel toegepast — ${field}` });
      load();
    } catch {
      toast({ title: "Toepassen mislukt", variant: "destructive" });
    }
  }

  async function resetOverride(slug, jur) {
    if (!confirm(`Override voor ${slug}/${jur} verwijderen?`)) return;
    await api.delete(`/admin/legal/rules/${slug}/${jur}`);
    toast({ title: "Override verwijderd → terug naar baseline" });
    load();
  }

  const outdatedRules = rules.filter((r) => r.audit?.is_outdated);
  const okRules = rules.filter((r) => r.audit && !r.audit.is_outdated);
  const noAuditRules = rules.filter((r) => !r.audit);

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-legal-page">
        {/* Info banner — uitleg verschil met /admin/legal-pages */}
        <div className="rounded-2xl bg-blue-50 border border-blue-200 p-5" data-testid="legal-rules-explain">
          <h3 className="font-display text-base font-semibold text-blue-900 flex items-center gap-2">
            <Scale className="h-4 w-4" /> Wat is &ldquo;Legal Rules&rdquo;?
          </h3>
          <p className="text-sm text-blue-900/80 mt-1.5 leading-relaxed">
            Dit zijn de <b>juridische regels die Claude AI verplicht volgt</b> bij elke documentanalyse — per checker × per regio (Vlaanderen / Brussel / Wallonië / Nederland / EU). Bijvoorbeeld: <i>&ldquo;huurwaarborg max 3 maanden in Vlaanderen&rdquo;</i>, <i>&ldquo;GDPR datalek-meldplicht 72u&rdquo;</i>. Deze constraints voorkomen dat de AI verzint of verkeerde wetten citeert.
            <br />
            <span className="text-xs">Verschil met <code className="bg-white px-1.5 py-0.5 rounded text-[11px]">/admin/legal-pages</code>: daar beheer je de drie publieke juridische pagina&rsquo;s (Privacy, Cookies, Algemene Voorwaarden) die bezoekers zien. Hier beheer je de interne wetskennis van de AI.</span>
          </p>
        </div>

        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold text-ink tracking-tight flex items-center gap-3">
              <Scale className="h-7 w-7 text-[#002FA7]" /> Legal Rules
            </h1>
            <p className="text-ink-soft mt-1 text-sm max-w-2xl">
              Backend-bron-van-waarheid voor Claude. Wekelijks (maandag 04:00) audit Claude alle regels en flagt mogelijke wetsupdates. Pas een suggestie aan via één klik — geen code-deploy nodig.
            </p>
          </div>
          <button
            onClick={runFullAudit}
            disabled={running}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white text-sm font-semibold disabled:opacity-50"
            data-testid="run-full-audit"
          >
            <RefreshCw className={`h-4 w-4 ${running ? "animate-spin" : ""}`} />
            {running ? "Audit loopt…" : "Run audit nu"}
          </button>
        </div>

        {/* Summary cards */}
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="rounded-2xl bg-white border border-border p-5">
            <div className="text-xs uppercase tracking-wider font-semibold text-ink-soft">Totaal regels</div>
            <div className="font-display text-3xl font-bold text-ink mt-2">{rules.length}</div>
            <div className="text-xs text-ink-soft mt-1">Checker × regio combinaties</div>
          </div>
          <div className="rounded-2xl bg-amber-50 border-2 border-amber-300 p-5" data-testid="outdated-count">
            <div className="text-xs uppercase tracking-wider font-semibold text-amber-700">Mogelijk verouderd</div>
            <div className="font-display text-3xl font-bold text-amber-700 mt-2">{summary?.outdated_count ?? "—"}</div>
            <div className="text-xs text-amber-600 mt-1">Vereisen menselijk nazicht</div>
          </div>
          <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5">
            <div className="text-xs uppercase tracking-wider font-semibold text-emerald-700">Laatst geaudit</div>
            <div className="font-display text-xl font-bold text-emerald-700 mt-2">
              {summary?.last_run?.finished_at ? new Date(summary.last_run.finished_at).toLocaleString("nl-BE") : "Nog niet gedraaid"}
            </div>
            <div className="text-xs text-emerald-600 mt-1">{summary?.total_audited ?? 0} gechecked</div>
          </div>
        </div>

        {/* Outdated section */}
        {outdatedRules.length > 0 && (
          <section data-testid="outdated-section">
            <h2 className="font-display text-xl font-semibold text-ink flex items-center gap-2 mb-3">
              <AlertTriangle className="h-5 w-5 text-amber-600" /> Mogelijk verouderd ({outdatedRules.length})
            </h2>
            <div className="space-y-4">
              {outdatedRules.map((r) => <RuleCard key={`${r.checker_slug}-${r.jurisdiction}`} rule={r} onAuditOne={auditOne} onApply={applySuggestion} onReset={resetOverride} onEdit={setEditing} />)}
            </div>
          </section>
        )}

        {okRules.length > 0 && (
          <section>
            <h2 className="font-display text-xl font-semibold text-ink flex items-center gap-2 mb-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Up-to-date volgens audit ({okRules.length})
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {okRules.map((r) => (
                <div key={`${r.checker_slug}-${r.jurisdiction}`} className="rounded-xl border border-border bg-white p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-display font-semibold text-ink truncate">{r.checker_slug}</h3>
                    <p className="text-xs text-ink-soft">{r.jurisdiction}</p>
                  </div>
                  <button onClick={() => auditOne(r.checker_slug, r.jurisdiction)} className="text-xs text-[#002FA7] hover:underline">Re-audit</button>
                </div>
              ))}
            </div>
          </section>
        )}

        {noAuditRules.length > 0 && (
          <section>
            <h2 className="font-display text-xl font-semibold text-ink-soft mb-3">Niet geaudit ({noAuditRules.length})</h2>
            <div className="flex flex-wrap gap-2">
              {noAuditRules.map((r) => (
                <button
                  key={`${r.checker_slug}-${r.jurisdiction}`}
                  onClick={() => auditOne(r.checker_slug, r.jurisdiction)}
                  className="text-xs px-3 py-1.5 rounded-full border border-border bg-white hover:bg-secondary"
                >
                  {r.checker_slug} · {r.jurisdiction}
                </button>
              ))}
            </div>
          </section>
        )}

        {editing && <EditRuleModal rule={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      </div>
    </AdminLayout>
  );
}


function RuleCard({ rule, onAuditOne, onApply, onReset, onEdit }) {
  const audit = rule.audit;
  return (
    <div className="rounded-2xl border-2 border-amber-300 bg-amber-50/40 p-5" data-testid={`rule-card-${rule.checker_slug}-${rule.jurisdiction}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-lg font-semibold text-ink">{rule.checker_slug} · <span className="text-ink-soft">{rule.jurisdiction}</span></h3>
          <p className="text-xs text-ink-soft mt-1">{rule.regime}</p>
          {rule.override_applied && (
            <span className="inline-block mt-2 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
              Override actief — {rule.override_updated_at?.slice(0, 16)}
            </span>
          )}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button onClick={() => onEdit(rule)} className="p-2 rounded-lg bg-white border border-border hover:bg-slate-50" title="Bewerken"><Edit className="h-4 w-4" /></button>
          {rule.override_applied && (
            <button onClick={() => onReset(rule.checker_slug, rule.jurisdiction)} className="p-2 rounded-lg bg-white border border-border hover:bg-slate-50" title="Reset naar baseline"><RotateCcw className="h-4 w-4" /></button>
          )}
          <button onClick={() => onAuditOne(rule.checker_slug, rule.jurisdiction)} className="p-2 rounded-lg bg-white border border-border hover:bg-slate-50" title="Re-audit"><RefreshCw className="h-4 w-4" /></button>
        </div>
      </div>

      {audit?.general_remarks && (
        <p className="text-sm text-ink mt-3 italic bg-white/60 p-3 rounded-lg">{audit.general_remarks}</p>
      )}

      {audit?.issues?.length > 0 && (
        <div className="mt-4 space-y-2">
          {audit.issues.map((issue, idx) => (
            <div key={idx} className="rounded-lg bg-white border border-amber-200 p-3" data-testid={`issue-${idx}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${SEVERITY_COLOR[issue.severity] || "bg-slate-100 text-slate-700"}`}>{issue.severity}</span>
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${CONFIDENCE_COLOR[issue.confidence] || "bg-slate-100 text-slate-700"}`}>{issue.confidence} vertrouwen</span>
                <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{issue.field}</code>
              </div>
              <p className="text-sm text-ink mt-2">{issue.reason}</p>
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span className="text-ink-soft">Huidig: <code className="bg-slate-100 px-1.5 py-0.5 rounded">{JSON.stringify(issue.current_value)}</code></span>
                {issue.suggested_value !== null && issue.suggested_value !== undefined && (
                  <>
                    <span className="text-ink-soft">→ Voorstel: <code className="bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">{JSON.stringify(issue.suggested_value)}</code></span>
                    <button
                      onClick={() => onApply(rule.checker_slug, rule.jurisdiction, issue.field, issue.suggested_value)}
                      className="ml-auto text-xs px-3 py-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                      data-testid={`apply-suggestion-${idx}`}
                    >
                      Toepassen
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function EditRuleModal({ rule, onClose, onSaved }) {
  const [regime, setRegime] = useState(rule.regime || "");
  const [constraintsJson, setConstraintsJson] = useState(JSON.stringify(rule.legal_constraints || {}, null, 2));
  const [referencesJson, setReferencesJson] = useState(JSON.stringify(rule.references || {}, null, 2));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  async function save() {
    let lc, refs;
    try {
      lc = JSON.parse(constraintsJson);
      refs = JSON.parse(referencesJson);
    } catch {
      toast({ title: "Ongeldige JSON", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/legal/rules/${rule.checker_slug}/${rule.jurisdiction}`, {
        regime, legal_constraints: lc, references: refs, note,
      });
      toast({ title: "Opgeslagen — actief vanaf nu" });
      onSaved();
    } catch {
      toast({ title: "Opslaan mislukt", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-rule-modal">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-border p-5 flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold text-ink">{rule.checker_slug} · {rule.jurisdiction}</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-full"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <Label>Regime (naam wet/decreet)</Label>
            <Input value={regime} onChange={(e) => setRegime(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label>Legal constraints (JSON)</Label>
            <textarea
              value={constraintsJson}
              onChange={(e) => setConstraintsJson(e.target.value)}
              className="w-full h-72 mt-1 font-mono text-xs p-3 border border-border rounded-lg"
            />
          </div>
          <div>
            <Label>References (JSON — wetsartikels die Claude mag citeren)</Label>
            <textarea
              value={referencesJson}
              onChange={(e) => setReferencesJson(e.target.value)}
              className="w-full h-44 mt-1 font-mono text-xs p-3 border border-border rounded-lg"
            />
          </div>
          <div>
            <Label>Notitie (wettelijke bron, datum wijziging, audit suggestion ID)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" placeholder="Bv. 'FOD Economie KB 12/03/2026 — JKP-grens verhoogd'" />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-full text-ink-soft hover:bg-secondary text-sm">Annuleer</button>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white text-sm font-semibold disabled:opacity-50">
              <Save className="h-4 w-4" /> {saving ? "Opslaan…" : "Opslaan"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
